import {
	Box3,
	BufferGeometry,
	CatmullRomCurve3,
	ConeGeometry,
	Euler,
	type LineSegments,
	Matrix4,
	Mesh,
	MeshBasicMaterial,
	TubeGeometry,
	Vector3
} from 'three';

import type { DebugManager } from '../../../managers/debug-manager';
import type { SceneManager } from '../../../managers/scene-manager';

export type CreateRotationAxisArcsParams = {
	debugManager: DebugManager;
	sceneManager: SceneManager;
};

/**
 * Radius of the rotation arc rings, in scene units (inches).
 */
const ARC_RADIUS = 14;

/**
 * Tube radius for the arc mesh (thickness of the arc line).
 */
const TUBE_RADIUS = 0.3;

/**
 * Tube radius for dim (inactive) arcs.
 */
const TUBE_RADIUS_DIM = 0.15;

/**
 * Number of points along the arc curve.
 */
const ARC_POINTS = 64;

/**
 * Arrowhead cone length and radius.
 */
const CONE_LENGTH = 3;
const CONE_RADIUS = 1;

/**
 * Axis colors for X (red), Y (green), Z (blue).
 */
const AXIS_COLORS = {
	x: 0xff2222,
	y: 0x22cc22,
	z: 0x2255ff
} as const;

/**
 * Dimmed axis colors for axes with zero rotation.
 */
const AXIS_COLORS_DIM = {
	x: 0x882222,
	y: 0x228822,
	z: 0x222288
} as const;

type RotationValues = { x: number; y: number; z: number };

/**
 * Generate points along an arc in a plane defined by two basis vectors.
 */
const getArcPoints = (
	center: Vector3,
	u: Vector3,
	v: Vector3,
	arcAngle: number,
	startAngle: number,
	numPoints: number
): Array<Vector3> => {
	const points: Array<Vector3> = [];
	for (let i = 0; i <= numPoints; i++) {
		const t = startAngle + (i / numPoints) * arcAngle;
		const point = center.clone()
			.addScaledVector(u, Math.cos(t) * ARC_RADIUS)
			.addScaledVector(v, Math.sin(t) * ARC_RADIUS);
		points.push(point);
	}
	return points;
};

/**
 * Create a tube mesh arc with an arrowhead cone at the tip.
 */
const createArcMesh = (
	id: string,
	center: Vector3,
	u: Vector3,
	v: Vector3,
	arcAngle: number,
	startAngle: number,
	color: number,
	isDim: boolean,
	debugManager: DebugManager
): void => {
	const points = getArcPoints(center, u, v, arcAngle, startAngle, ARC_POINTS);
	if (points.length < 2) return;

	const curve = new CatmullRomCurve3(points, false, 'catmullrom', 0);
	const tubeRadius = isDim ? TUBE_RADIUS_DIM : TUBE_RADIUS;
	const tubeGeo = new TubeGeometry(curve, ARC_POINTS, tubeRadius, 6, false);
	const material = new MeshBasicMaterial({
		color,
		depthTest: false,
		transparent: isDim,
		opacity: isDim ? 0.5 : 1
	});

	const tubeMesh = new Mesh(tubeGeo, material);
	tubeMesh.name = `debug_rotation_arc_${id}`;
	tubeMesh.visible = true;
	tubeMesh.renderOrder = 999;
	tubeMesh.userData.isInterface = true;
	tubeMesh.userData.type = 'debugVisualization';
	tubeMesh.userData.isRotationArc = true;

	debugManager.scene.add(tubeMesh);
	debugManager.registerLine(id, tubeMesh as unknown as LineSegments);

	// Arrowhead cone at the tip
	const tipAngle = startAngle + arcAngle;
	const tip = center.clone()
		.addScaledVector(u, Math.cos(tipAngle) * ARC_RADIUS)
		.addScaledVector(v, Math.sin(tipAngle) * ARC_RADIUS);

	// Tangent at the tip for cone orientation
	const sign = arcAngle >= 0 ? 1 : -1;
	const tangent = u.clone()
		.multiplyScalar(-Math.sin(tipAngle) * sign)
		.addScaledVector(v, Math.cos(tipAngle) * sign)
		.normalize();

	const coneLength = isDim ? CONE_LENGTH * 0.7 : CONE_LENGTH;
	const coneRadius = isDim ? CONE_RADIUS * 0.7 : CONE_RADIUS;
	const coneGeo = new ConeGeometry(coneRadius, coneLength, 8);
	const coneMaterial = new MeshBasicMaterial({
		color,
		depthTest: false,
		transparent: isDim,
		opacity: isDim ? 0.5 : 1
	});

	const cone = new Mesh(coneGeo, coneMaterial);
	// Position cone at the tip, shifted forward half its length along the tangent
	cone.position.copy(tip).addScaledVector(tangent, coneLength / 2);
	// Orient cone to point along tangent
	cone.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), tangent);

	cone.name = `debug_rotation_arrow_${id}`;
	cone.visible = true;
	cone.renderOrder = 999;
	cone.userData.isInterface = true;
	cone.userData.type = 'debugVisualization';
	cone.userData.isRotationArc = true;

	debugManager.scene.add(cone);
	const arrowId = `rotation-arrow-${id}`;
	debugManager.registerLine(arrowId, cone as unknown as LineSegments);
};

/**
 * Remove all rotation arc meshes that were previously registered.
 */
const clearExistingRotationArcs = (debugManager: DebugManager): void => {
	const toRemove: Array<string> = [];

	for (const [id, line] of debugManager.lines.entries()) {
		if (line.userData?.isRotationArc) {
			toRemove.push(id);
		}
	}

	for (const id of toRemove) {
		debugManager.removeLine(id);
	}
};

/**
 * Create rotation axis arc visualizations for all appliances in the scene.
 *
 * Every appliance shows all three axis arcs:
 *   X rotation -> RED arc in the YZ plane
 *   Y rotation -> GREEN arc in the XZ plane
 *   Z rotation -> BLUE arc in the XY plane
 *
 * Active rotations (non-zero) are bright and thick.
 * Inactive rotations (zero) are dim and thin, showing a default quarter-circle.
 */
export const createRotationAxisArcsCfn = (params: CreateRotationAxisArcsParams): void => {
	const { debugManager, sceneManager } = params;

	if (!sceneManager?.scene) return;

	clearExistingRotationArcs(debugManager);

	const { scene } = sceneManager;
	const worldPos = new Vector3();
	const defaultArc = Math.PI / 2;

	scene.traverse((object) => {
		if (!object.visible) return;

		const { type, id, modelRotation } = object.userData as {
			type?: string;
			id?: string;
			modelRotation?: RotationValues;
		};

		if (type !== 'appliance') return;

		const mr: RotationValues = modelRotation ?? { x: 0, y: 0, z: 0 };

		// Use the bounding box center (visual center) rather than the pivot point
		const bbox = new Box3().setFromObject(object);
		const center = new Vector3();
		bbox.getCenter(center);

		// Compute the arc axes in the appliance's BASE frame — the coordinate
		// system BEFORE modelRotation is applied. This is the island/panel frame:
		// parent world rotation (island orientation) + base panel angle on Z.
		//
		// For the engine pivot path: the object is a pivot Group with
		// rotation.z = panelAngle + mr.z. X/Y modelRotation lives on an inner
		// rotationGroup, NOT on the pivot — so only Z needs mr.z subtracted.
		//
		// For the direct path: the object is the Collada scene with Euler angles
		// that include both the Collada built-in and modelRotation. We still only
		// use Z for the base panel angle, keeping the arc axes aligned with the
		// intuitive island/panel frame rather than the Collada-internal frame.
		const basePanelAngle = object.rotation.z - (mr.z || 0);
		const baseEuler = new Euler(0, 0, basePanelAngle, 'XYZ');
		const baseRotation = new Matrix4().makeRotationFromEuler(baseEuler);

		if (object.parent) {
			object.parent.updateWorldMatrix(true, false);
			const parentRotation = new Matrix4().extractRotation(object.parent.matrixWorld);
			baseRotation.premultiply(parentRotation);
		}

		const localX = new Vector3(1, 0, 0).applyMatrix4(baseRotation);
		const localY = new Vector3(0, 1, 0).applyMatrix4(baseRotation);
		const localZ = new Vector3(0, 0, 1).applyMatrix4(baseRotation);

		const applianceId = id ?? object.name ?? object.uuid.slice(0, 8);

		// X rotation: RED arc in local YZ plane
		createArcMesh(
			`x-${applianceId}`, center,
			localY, localZ,
			mr.x !== 0 ? mr.x : defaultArc, 0,
			mr.x !== 0 ? AXIS_COLORS.x : AXIS_COLORS_DIM.x,
			mr.x === 0, debugManager
		);

		// Y rotation: GREEN arc in local XZ plane
		createArcMesh(
			`y-${applianceId}`, center,
			localX, localZ,
			mr.y !== 0 ? mr.y : defaultArc, 0,
			mr.y !== 0 ? AXIS_COLORS.y : AXIS_COLORS_DIM.y,
			mr.y === 0, debugManager
		);

		// Z rotation: BLUE arc in local XY plane
		createArcMesh(
			`z-${applianceId}`, center,
			localX, localY,
			mr.z !== 0 ? mr.z : defaultArc, 0,
			mr.z !== 0 ? AXIS_COLORS.z : AXIS_COLORS_DIM.z,
			mr.z === 0, debugManager
		);
	});
};
